

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { UserRole } from '@/shared/enums/UserRoles';
import { usePermissions, PermissionCheckOptions } from '@/shared/hooks/usePermissions';
import { useAuth } from '@/contexts/auth.context';

interface PermissionGuardProps {
  children: React.ReactNode;
  
  permissions?: UserPermissions | UserPermissions[];
  
  roles?: UserRole | UserRole[];
  
  options?: PermissionCheckOptions;
  
  fallback?: React.ReactNode;
  redirectTo?: string;
  showError?: boolean;
  
  mode?: 'route' | 'element';
}

export function PermissionGuard({
  children,
  permissions,
  roles,
  options = {},
  fallback,
  redirectTo = '/',
  showError = true,
  mode = 'element'
}: PermissionGuardProps): JSX.Element | null {
  const { isAuthenticated, loading } = useAuth();
  const { hasPermission, hasRole } = usePermissions();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    if (mode === 'route') {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    return null;
  }

  let hasRequiredPermissions = true;
  
  if (permissions) {
    hasRequiredPermissions = hasPermission(permissions, options);
  }
  
  if (roles && hasRequiredPermissions) {
    hasRequiredPermissions = hasRole(roles, options);
  }

  if (!hasRequiredPermissions) {    
    if (fallback) {
      return <>{fallback}</>;
    }

    if (mode === 'route') {
      return <Navigate to={redirectTo} replace />;
    }

    if (showError) {
      return (
        <Result
          status="403"
          title="403"
          subTitle="Você não tem permissão para acessar este recurso."
          extra={
            <a href="/" style={{ color: '#1890ff' }}>
              Voltar ao início
            </a>
          }
        />
      );
    }

    return null;
  }

  return <>{children}</>;
}

export function RouteGuard(props: Omit<PermissionGuardProps, 'mode'>) {
  return <PermissionGuard {...props} mode="route" />;
}

export function ElementGuard(props: Omit<PermissionGuardProps, 'mode'>) {
  return <PermissionGuard {...props} mode="element" showError={false} />;
}

export function withPermissions<P extends object>(
  Component: React.ComponentType<P>,
  guardProps: Omit<PermissionGuardProps, 'children' | 'mode'>
) {
  return function PermissionWrappedComponent(props: P) {
    return (
      <ElementGuard {...guardProps}>
        <Component {...props} />
      </ElementGuard>
    );
  };
}